import axios, { CancelToken } from 'axios';

const partnershipBackendUrl = process.env.REACT_APP_PARTNERSHIP_BACKEND_URL || 'http://localhost:3000';
const partnershipApiKey = process.env.REACT_APP_PARTNERSHIP_API_KEY;

const axiosInstance = axios.create({
  baseURL: partnershipBackendUrl,
});

const NETWORK_RETRY_DELAY_MS = 500;
const NETWORK_RETRY_MAX = 1;

function isLikelyNetworkFailure(err) {
  if (!err || err.response) {
    return false;
  }
  const { code } = err;
  const msg = String(err.message || '');
  return (
    code === 'ECONNABORTED' ||
    code === 'ERR_NETWORK' ||
    msg.includes('Network Error') ||
    msg.includes('ERR_NETWORK') ||
    msg.includes('Network changed')
  );
}

function mapAxiosError(err) {
  let error = null;

  switch (err.constructor) {
    case SyntaxError:
    case TypeError: {
      error = `Não foi possível se comunicar com o servidor: ${err.message}`;
      break;
    }
    case DOMException: {
      error = null;
      break;
    }
    default: {
      if (err.response?.data?.message || err.response?.data?.error) {
        error = err.response.data.message || err.response.data.error;
      } else if (err.message) {
        error = err.message;
      } else {
        error = `Erro ao comunicar com o servidor (${err.response?.status || 'N/A'})`;
      }
      break;
    }
  }
  return Promise.reject(error);
}

function makeRequest(method, path, { token, params, body, contentType, ...options }) {
  const headers = {};
  const isBinaryResponse = options.responseType === 'blob' || options.responseType === 'arraybuffer';

  headers.Accept = isBinaryResponse ? '*/*' : 'application/json';
  headers['Accept-Language'] = navigator.language.toLowerCase();

  // GET/HEAD sem corpo: não enviar Content-Type (evita preflight CORS desnecessário e não muda o contrato da API).
  const setsJsonContentType =
    contentType !== false && !(method === 'GET' && isBinaryResponse) && method !== 'GET' && method !== 'HEAD';
  if (setsJsonContentType) {
    headers['Content-Type'] = contentType || 'application/json';
  }

  if (partnershipApiKey) {
    headers['x-api-key'] = partnershipApiKey;
  } else if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const allowNetworkRetry = (method === 'GET' || method === 'HEAD') && !isBinaryResponse && NETWORK_RETRY_MAX > 0;
  const attempts = 1 + (allowNetworkRetry ? NETWORK_RETRY_MAX : 0);

  let cancelLastRequest = () => {};

  const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

  const promise = (async () => {
    async function runAttempt(attemptIndex) {
      const controller = CancelToken.source();
      cancelLastRequest = () => controller.cancel('Cancelled by user');
      try {
        const res = await axiosInstance.request({
          url: path,
          method,
          headers,
          params,
          data: body,
          cancelToken: controller.token,
          ...options,
        });
        return res.data;
      } catch (err) {
        const canRetry = allowNetworkRetry && attemptIndex < attempts - 1 && isLikelyNetworkFailure(err);
        if (canRetry) {
          await delay(NETWORK_RETRY_DELAY_MS);
          return runAttempt(attemptIndex + 1);
        }
        if (!err) {
          return Promise.reject(new Error('Erro desconhecido ao comunicar com o servidor'));
        }
        return mapAxiosError(err);
      }
    }

    return runAttempt(0);
  })();

  promise.cancel = () => cancelLastRequest();

  return promise;
}

export { axiosInstance };

export default {
  get: (path, options = {}) => makeRequest('GET', path, options),
  post: (path, body, options = {}) => makeRequest('POST', path, { ...options, body }),
  put: (path, body, options = {}) => makeRequest('PUT', path, { ...options, body }),
  patch: (path, body, options = {}) => makeRequest('PATCH', path, { ...options, body }),
  delete: (path, options = {}) => makeRequest('DELETE', path, options),
};
